// src/tour/components/TourOverlay.js
//
// The single root-mounted overlay that renders whatever the active tour step
// needs: a centered choice card, an anchored bubble with a spotlight, a live
// "in-flow" bubble that floats over the working app, or a sequential pointer
// tour. Easy-out controls (Skip step / Skip tour / Remind me later) live in a
// fixed footer that is always reachable.
//
// Anchors are resolved from the DOM via [data-tour-anchor="..."], so the
// overlay never needs refs threaded in from host components.

import { useState, useEffect, useLayoutEffect } from 'react'
import { useTour } from '../useTour'
import ChoiceCard from './ChoiceCard'
import TourBubble from './TourBubble'
import { TC, touchBtn } from './tourTheme'
import { onTourEvent, getLastTourEvent, clearLastTourEvent, TOUR_EVENT_TYPES } from '../../lib/appEvents'

const Z = { catcher: 9990, spotlight: 9991, bubble: 9995, footer: 9999 }

// ---------------------------------------------------------------------------
// anchor measurement
// ---------------------------------------------------------------------------
function useAnchorRect(anchorId) {
  const [rect, setRect] = useState(null)
  useLayoutEffect(() => {
    if (!anchorId) { setRect(null); return }
    let raf, count = 0
    const measure = () => {
      const el = document.querySelector(`[data-tour-anchor="${anchorId}"]`)
      if (!el) { setRect(null); return }
      const r = el.getBoundingClientRect()
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height, bottom: r.bottom })
    }
    measure()
    // Re-measure for ~0.5s to catch layout settling after tab switches / modal opens.
    const tick = () => { measure(); if (++count < 30) raf = requestAnimationFrame(tick) }
    raf = requestAnimationFrame(tick)
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
  }, [anchorId])
  return rect
}

function computeBubblePosition(rect) {
  const vw = window.innerWidth, vh = window.innerHeight
  const width = Math.min(420, vw - 16)
  if (!rect) {
    return { left: (vw - width) / 2, top: null, bottom: 88, width, placement: null }
  }
  const below = rect.top < vh / 2
  const centerX = rect.left + rect.width / 2
  const left = Math.max(8, Math.min(centerX - width / 2, vw - width - 8))
  return below
    ? { left, top: rect.bottom + 12, bottom: null, width, placement: 'below' }
    : { left, top: null, bottom: vh - rect.top + 12, width, placement: 'above' }
}

// ---------------------------------------------------------------------------
// fixed easy-out footer
// ---------------------------------------------------------------------------
function EasyOutFooter({ canBack, onBack, onSkipStep, onSkipTour, onRemind }) {
  const linkBtn = {
    background: 'none', border: 'none', cursor: 'pointer',
    fontFamily: TC.fBody, fontSize: 13, color: '#fff', opacity: 0.92, padding: '8px 6px',
    minHeight: 44,
  }
  return (
    <div style={{
      position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: Z.footer,
      display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 14,
      padding: '6px 12px', background: 'rgba(44,29,20,0.78)', pointerEvents: 'auto',
    }}>
      {canBack && <button style={linkBtn} onClick={onBack}>Back</button>}
      <button style={linkBtn} onClick={onSkipStep}>Skip step</button>
      <button style={{ ...linkBtn, fontWeight: 600 }} onClick={onSkipTour}>Skip tour</button>
      <button style={linkBtn} onClick={onRemind}>Remind me later</button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// per-step renderer (remounted per step via key, so local state is fresh)
// ---------------------------------------------------------------------------
function StepRenderer() {
  const { state, actions } = useTour()
  const step = state.currentStep

  const steps = state.activeScript.steps
  const progressSteps = steps.filter(s => s.countsTowardProgress)
  const dotsTotal = progressSteps.length
  const dotsCurrent = progressSteps.findIndex(s => s.id === step.id)

  // pointer_tour local cursor
  const [pointerIdx, setPointerIdx] = useState(0)
  const isPointer = step.type === 'pointer_tour'
  const pointer = isPointer ? step.pointers?.[pointerIdx] : null

  // wow_moment local result
  const [wow, setWow] = useState(null) // { status, manual_url, make, model } | null
  const [timedOut, setTimedOut] = useState(false)
  const isWow = step.type === 'wow_moment'

  useEffect(() => {
    if (!isWow) return
    // Pick up a result that may have resolved a beat before this step mounted.
    const cached = getLastTourEvent(TOUR_EVENT_TYPES.MANUAL_LOOKUP_RESOLVED, { maxAgeMs: 60000 })
    if (cached) {
      setWow(cached)
      clearLastTourEvent(TOUR_EVENT_TYPES.MANUAL_LOOKUP_RESOLVED)
      actions.track(cached.status === 'hit' ? 'wow_moment_hit' : 'wow_moment_miss',
        { stepId: step.id, eventData: { make: cached.make, model: cached.model } })
      return
    }
    const off = onTourEvent(TOUR_EVENT_TYPES.MANUAL_LOOKUP_RESOLVED, (payload) => {
      setWow(payload)
      actions.track(payload.status === 'hit' ? 'wow_moment_hit' : 'wow_moment_miss',
        { stepId: step.id, eventData: { make: payload.make, model: payload.model } })
    })
    const t = setTimeout(() => setTimedOut(true), 10000)
    return () => { off(); clearTimeout(t) }
  }, [isWow, step.id, actions])

  // wow_moment: on a HIT, auto-advance after 1s (Continue is still available).
  useEffect(() => {
    if (isWow && wow?.status === 'hit') {
      const t = setTimeout(() => actions.nextStep(), 1000)
      return () => clearTimeout(t)
    }
  }, [isWow, wow, actions])

  // anchor for this step (or current pointer)
  const anchorId = isPointer ? pointer?.anchor : step.anchor
  const rect = useAnchorRect(anchorId)
  const pos = computeBubblePosition(rect)

  const isBlocking = step.type !== 'in_flow'

  // ---- handlers ----
  const handleCta = (cta) => {
    if (!cta) return
    if (cta.action === 'continue') { actions.nextStep(); return }
    actions.dispatchAction(cta.action)
    // Wait-steps open the real flow but advance via signal, not the CTA click.
    if (!step.waitsForSignal) actions.nextStep()
  }
  const handlePointerNext = () => {
    if (pointerIdx < (step.pointers.length - 1)) setPointerIdx(i => i + 1)
    else actions.completeTour()
  }

  // ===== choice card (centered, blocking) =====
  if (step.type === 'choice_card') {
    return (
      <>
        <div style={{ position: 'fixed', inset: 0, zIndex: Z.catcher, background: TC.backdrop }} />
        <div style={{
          position: 'fixed', inset: 0, zIndex: Z.bubble, display: 'flex',
          alignItems: 'center', justifyContent: 'center', padding: 16,
        }}>
          <ChoiceCard
            title={step.title}
            body={step.body}
            options={step.options}
            onSelect={(opt) => {
              if (opt.action === 'skip_tour') actions.skipTour(opt.dismissReason || 'user_skipped')
              else actions.nextStep()
            }}
          />
        </div>
      </>
    )
  }

  // ===== bubble content (instruction / in_flow / pointer / wow) =====
  let title = step.title
  let body = step.body
  let cta = step.cta
  let footerLine = step.footerLine

  if (isPointer && pointer) {
    title = pointer.label
    body = pointer.body || step.body
    cta = null
    footerLine = null
  }

  if (isWow) {
    cta = null
    if (wow?.status === 'hit') { title = step.onHit.title; body = step.onHit.body; cta = step.onHit.cta }
    else if (wow && wow.status !== 'hit') { title = step.onMiss.title; body = step.onMiss.body; cta = step.onMiss.cta }
    else if (timedOut) { title = step.onTimeout.title; body = step.onTimeout.body; cta = step.onTimeout.cta }
  }

  // in_flow steps without their own CTA still need forward motion
  const inFlowContinue = step.type === 'in_flow' && !isWow && !cta && !step.waitsForSignal

  const bubble = (
    <div style={{
      position: 'fixed', zIndex: Z.bubble, width: pos.width,
      left: pos.left, top: pos.top ?? undefined, bottom: pos.bottom ?? undefined,
      pointerEvents: 'auto',
    }}>
      <TourBubble
        title={title}
        body={body}
        footerLine={footerLine}
        cta={cta}
        onCta={() => handleCta(cta)}
        arrowPlacement={rect ? pos.placement : null}
        dotsTotal={dotsTotal}
        dotsCurrent={dotsCurrent}
      >
        {isWow && !wow && !timedOut && (
          <div style={{ marginTop: 12, fontSize: 13, color: TC.muted, fontFamily: TC.fBody }}>
            Searching the web&hellip;
          </div>
        )}
        {inFlowContinue && (
          <button
            onClick={() => actions.nextStep()}
            style={{ ...touchBtn, marginTop: 16, width: '100%', border: 'none', background: TC.primary, color: '#fff', fontWeight: 600 }}
          >
            Continue
          </button>
        )}
        {isPointer && (
          <button
            onClick={handlePointerNext}
            style={{ ...touchBtn, marginTop: 16, width: '100%', border: 'none', background: TC.primary, color: '#fff', fontWeight: 600 }}
          >
            {pointerIdx < step.pointers.length - 1 ? 'Next' : 'Finish'}
          </button>
        )}
      </TourBubble>
    </div>
  )

  return (
    <>
      {/* click catcher / dimming */}
      {isBlocking ? (
        rect ? (
          // spotlight: the box-shadow dims everything except the anchor hole
          <>
            <div style={{ position: 'fixed', inset: 0, zIndex: Z.catcher, pointerEvents: 'auto' }} />
            <div style={{
              position: 'fixed', zIndex: Z.spotlight, pointerEvents: 'none',
              top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12,
              borderRadius: 10, border: `2px solid ${TC.primary}`,
              boxShadow: `0 0 0 9999px ${TC.backdrop}`,
            }} />
          </>
        ) : (
          <div style={{ position: 'fixed', inset: 0, zIndex: Z.catcher, background: TC.backdrop, pointerEvents: 'auto' }} />
        )
      ) : (
        // in_flow: don't block the app; just a subtle ring on the anchor
        rect && (
          <div style={{
            position: 'fixed', zIndex: Z.spotlight, pointerEvents: 'none',
            top: rect.top - 4, left: rect.left - 4, width: rect.width + 8, height: rect.height + 8,
            borderRadius: 10, border: `2px solid ${TC.primary}`,
          }} />
        )
      )}

      {bubble}

      <EasyOutFooter
        canBack={state.currentStepIndex > 0 && !isPointer}
        onBack={actions.previousStep}
        onSkipStep={actions.skipStep}
        onSkipTour={() => actions.skipTour('user_skipped')}
        onRemind={actions.remindLater}
      />
    </>
  )
}

export default function TourOverlay() {
  const { state } = useTour()
  if (!state.activeScript || !state.isPaused || !state.currentStep) return null
  return <StepRenderer key={state.currentStep.id} />
}
